import React, {useEffect, useState} from 'react';
import dayjs from 'dayjs';

import http from 'services/http';
import {alerta} from 'components/Alert';
import {useGetUserPermission} from 'hook/permissions';
import Pagination from 'components/Paginacao';
import 'components/Paginacao/style.css';
import {trataErroEsportivas, AvisoModuloBloqueado} from '../shared';

import styles from './styles.module.css';

// Guarda a ordem persistida para o onBlur só salvar quando houver mudança
function marcaOrdemSalva(itens) {
    return itens.map((item) => ({...item, ordem_salva: item.curadoria_listing_order}));
}

// Ordena pela ordem de exibição da banca (menor primeiro, null vai pro fim)
function ordenaCuradoria(itens) {
    return [...itens].sort((a, b) => {
        const ordemA = a.curadoria_listing_order ?? Infinity;
        const ordemB = b.curadoria_listing_order ?? Infinity;

        if (ordemA !== ordemB) {
            return ordemA - ordemB;
        }

        return a.name.localeCompare(b.name);
    });
}

export default function Curadoria() {
    const [esportes, setEsportes] = useState([]);
    const [ligas, setLigas] = useState([]);
    const [esporteSelecionado, setEsporteSelecionado] = useState(null);

    const [eventos, setEventos] = useState([]);
    const [eventosMeta, setEventosMeta] = useState(null);
    const [paginaEventos, setPaginaEventos] = useState(1);
    const [buscaEvento, setBuscaEvento] = useState('');
    const [mostrarEventos, setMostrarEventos] = useState(false);
    const [eventosDesativados, setEventosDesativados] = useState([]);

    const [eventoMercados, setEventoMercados] = useState(null);
    const [mercados, setMercados] = useState([]);
    const [mercadosDesativados, setMercadosDesativados] = useState([]);

    const [isLoading, setIsLoading] = useState(true);
    const [carregaLigas, setCarregaLigas] = useState(false);
    const [carregaEventos, setCarregaEventos] = useState(false);
    const [carregaPut, setCarregaPut] = useState(false);
    const [moduloBloqueado, setModuloBloqueado] = useState(false);

    const permissoes = useGetUserPermission();
    const podeEditar = permissoes && permissoes.includes('Editar settings esportivas');

    useEffect(() => {
        const requestEsportes = async () => {
            try {
                const response = await http.get('/esportivas/curadoria/esportes');
                setEsportes(ordenaCuradoria(marcaOrdemSalva(response.data.data)));
            } catch (e) {
                trataErroEsportivas(e, setModuloBloqueado);
            }

            setIsLoading(false);
        };

        requestEsportes();
    }, []);

    const requestLigas = async (esporte) => {
        setEsporteSelecionado(esporte);
        setCarregaLigas(true);

        try {
            const response = await http.get('/esportivas/curadoria/ligas', {
                params: {sport_external_id: esporte.external_id},
            });

            setLigas(ordenaCuradoria(marcaOrdemSalva(response.data.data)));
        } catch (e) {
            trataErroEsportivas(e, setModuloBloqueado);
        }

        setCarregaLigas(false);
    };

    const requestEventos = async (pagina = 1) => {
        setMostrarEventos(true);
        setCarregaEventos(true);
        setPaginaEventos(pagina);

        try {
            const response = await http.get('/esportivas/eventos', {
                params: {
                    page: pagina,
                    search: buscaEvento || undefined,
                    sport_external_id: esporteSelecionado?.external_id || undefined,
                },
            });

            setEventos(response.data.data.data);
            setEventosMeta(response.data.data.meta);
        } catch (e) {
            trataErroEsportivas(e, setModuloBloqueado);
        }

        setCarregaEventos(false);
    };

    // PUT genérico de curadoria (esportes/ligas). O parâmetro da URL é o external_id.
    const salvarCuradoria = async (recurso, item, dados, aoSalvar) => {
        try {
            setCarregaPut(true);

            const response = await http.put(
                `/esportivas/curadoria/${recurso}/${item.external_id}`,
                dados
            );

            aoSalvar();
            alerta(response.data);
        } catch (e) {
            trataErroEsportivas(e, setModuloBloqueado);
        }

        setCarregaPut(false);
    };

    const alternarEsporte = (esporte) => {
        const ativo = !esporte.curadoria_active;

        salvarCuradoria('esportes', esporte, {
            active: ativo,
            listing_order: esporte.curadoria_listing_order,
        }, () => {
            setEsportes((prev) => prev.map((item) =>
                item.external_id === esporte.external_id
                    ? {...item, curadoria_active: ativo}
                    : item
            ));
        });
    };

    const alternarLiga = (liga) => {
        const ativo = !liga.curadoria_active;

        salvarCuradoria('ligas', liga, {
            active: ativo,
            listing_order: liga.curadoria_listing_order,
        }, () => {
            setLigas((prev) => prev.map((item) =>
                item.external_id === liga.external_id
                    ? {...item, curadoria_active: ativo}
                    : item
            ));
        });
    };

    const mudarOrdem = (setLista, externalId, valor) => {
        const ordem = valor === '' ? null : parseInt(valor, 10);

        setLista((prev) => prev.map((item) =>
            item.external_id === externalId
                ? {...item, curadoria_listing_order: isNaN(ordem) ? null : ordem}
                : item
        ));
    };

    const salvarOrdem = (recurso, item, setLista) => {
        if (item.curadoria_listing_order === item.ordem_salva) {
            return;
        }

        salvarCuradoria(recurso, item, {
            active: item.curadoria_active,
            listing_order: item.curadoria_listing_order,
        }, () => {
            setLista((prev) => ordenaCuradoria(prev.map((outro) =>
                outro.external_id === item.external_id
                    ? {...outro, ordem_salva: item.curadoria_listing_order}
                    : outro
            )));
        });
    };

    const alternarEvento = (evento, ativo) => {
        salvarCuradoria('eventos', evento, {active: ativo}, () => {
            setEventosDesativados((prev) => ativo
                ? prev.filter((id) => id !== evento.external_id)
                : [...prev, evento.external_id]);
        });
    };

    const abrirMercados = async (evento) => {
        try {
            const response = await http.get(`/esportivas/eventos/${evento.id}/odds`);

            setEventoMercados(evento);
            setMercados(response.data.data);
        } catch (e) {
            trataErroEsportivas(e, setModuloBloqueado);
        }
    };

    const alternarMercado = (mercado, ativo) => {
        salvarCuradoria('mercados', mercado, {active: ativo}, () => {
            setMercadosDesativados((prev) => ativo
                ? prev.filter((id) => id !== mercado.external_id)
                : [...prev, mercado.external_id]);
        });
    };

    const tabelaCuradoria = (titulo, lista, recurso, setLista, alternar) => (
        <div className="conteudo-principal p-0 mt-2">
            <table className="table text-center border">
                <thead>
                <tr>
                    <th className="text-left">{titulo}</th>
                    <th>Ordem</th>
                    <th>Status</th>
                    {titulo === 'Esporte' && <th>Ligas</th>}
                </tr>
                </thead>
                <tbody>
                {lista.map((item) => (
                    <tr key={item.external_id}>
                        <td className="text-left align-middle">
                            {item.name}
                            {!item.active && (
                                <span className="badge badge-secondary ml-2">inativo no catálogo</span>
                            )}
                        </td>
                        <td className={styles.colunaOrdem}>
                            <input
                                type="number"
                                inputMode="numeric"
                                min={0}
                                className="form-control"
                                disabled={!podeEditar || carregaPut}
                                value={item.curadoria_listing_order ?? ''}
                                placeholder="—"
                                onChange={(e) => mudarOrdem(setLista, item.external_id, e.target.value)}
                                onBlur={() => salvarOrdem(recurso, item, setLista)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.target.blur();
                                    }
                                }}
                            />
                        </td>
                        <td className="align-middle">
                            <button
                                className={`btn ${item.curadoria_active ? 'btn-success' : 'btn-danger'}`}
                                disabled={!podeEditar || carregaPut}
                                onClick={() => alternar(item)}
                            >
                                {item.curadoria_active ? 'Ativo' : 'Bloqueado'}
                            </button>
                        </td>
                        {titulo === 'Esporte' && (
                            <td className="align-middle">
                                <button
                                    className="btn btn-info"
                                    disabled={carregaLigas}
                                    onClick={() => requestLigas(item)}
                                >
                                    Ver ligas
                                </button>
                            </td>
                        )}
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );

    const modalMercados = () => (eventoMercados && (
        <div className="modal" style={{display: 'block', overflowY: 'scroll', maxHeight: '100%'}}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-body">
                        <div className={styles.tituloModal}>
                            Mercados — {eventoMercados.team_home?.name} x {eventoMercados.team_out?.name}
                        </div>
                        <small className="text-muted d-block mb-2">
                            Bloquear um mercado vale para a banca toda (todos os eventos), não só para este jogo.
                        </small>

                        {mercados.length === 0 && (
                            <div className="p-2">Nenhum mercado ativo para este evento.</div>
                        )}

                        {mercados.length > 0 && (
                            <table className="table text-center border">
                                <thead>
                                <tr>
                                    <th className="text-left">Mercado</th>
                                    <th>Grupo</th>
                                    <th>Status</th>
                                </tr>
                                </thead>
                                <tbody>
                                {mercados.map(({market}) => {
                                    const desativado = mercadosDesativados.includes(market.external_id);

                                    return (
                                        <tr key={market.external_id}>
                                            <td className="text-left align-middle">{market.name}</td>
                                            <td className="align-middle">{market.group}</td>
                                            <td className="align-middle">
                                                <button
                                                    className={`btn ${desativado ? 'btn-danger' : 'btn-success'}`}
                                                    disabled={!podeEditar || carregaPut}
                                                    onClick={() => alternarMercado(market, desativado)}
                                                >
                                                    {desativado ? 'Bloqueado' : 'Ativo'}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button
                            style={{width: '100%'}}
                            type="button"
                            className="btn btn-danger"
                            onClick={() => setEventoMercados(null)}
                        >
                            Fechar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    ));

    if (moduloBloqueado) {
        return (
            <>
                <div className="titulo-principal">Curadoria de Esportivas</div>
                <AvisoModuloBloqueado/>
            </>
        );
    }

    return (
        <>
            <div className="titulo-principal">Curadoria de Esportivas</div>

            {isLoading && <div className="loader"/>}

            {!isLoading && (
                <>
                    {!podeEditar && (
                        <div className="conteudo-principal mt-2" style={{padding: 10}}>
                            Você não possui a permissão "Editar settings esportivas" — somente leitura.
                        </div>
                    )}

                    {esportes.length === 0 && (
                        <div className="conteudo-principal mt-2" style={{padding: 10}}>
                            Nenhum esporte no catálogo ainda.
                        </div>
                    )}

                    {esportes.length > 0 && tabelaCuradoria('Esporte', esportes, 'esportes', setEsportes, alternarEsporte)}

                    {esporteSelecionado && (
                        <>
                            <div className={styles.tituloSecao}>
                                Ligas de {esporteSelecionado.name}
                            </div>

                            {carregaLigas && <div className="loader"/>}

                            {!carregaLigas && ligas.length === 0 && (
                                <div className="conteudo-principal mt-2" style={{padding: 10}}>
                                    Nenhuma liga no catálogo para este esporte.
                                </div>
                            )}

                            {!carregaLigas && ligas.length > 0 &&
                                tabelaCuradoria('Liga', ligas, 'ligas', setLigas, alternarLiga)}
                        </>
                    )}

                    <div className={styles.tituloSecao}>Curadoria fina — eventos e mercados</div>

                    <div className="conteudo-principal mt-2" style={{padding: 10}}>
                        <small className="text-muted d-block mb-2">
                            A lista mostra apenas eventos ativos{esporteSelecionado ? ` de ${esporteSelecionado.name}` : ''}.
                            Eventos bloqueados aqui somem da lista ao recarregar — o desbloqueio só fica
                            disponível nesta sessão, logo após o bloqueio.
                        </small>

                        <div className="d-flex duas-colunas">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Buscar por nome do time"
                                value={buscaEvento}
                                onChange={(e) => setBuscaEvento(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        requestEventos(1);
                                    }
                                }}
                            />
                            <button
                                className="btn btn-primary"
                                style={{height: '38px'}}
                                disabled={carregaEventos}
                                onClick={() => requestEventos(1)}
                            >
                                Buscar eventos
                            </button>
                        </div>
                    </div>

                    {carregaEventos && <div className="loader"/>}

                    {!carregaEventos && mostrarEventos && eventos.length === 0 && (
                        <div className="conteudo-principal mt-2" style={{padding: 10}}>
                            Nenhum evento encontrado.
                        </div>
                    )}

                    {!carregaEventos && eventos.length > 0 && (
                        <div className="conteudo-principal p-0 mt-2">
                            <table className="table text-center border">
                                <thead>
                                <tr>
                                    <th>Data</th>
                                    <th>Esporte / Liga</th>
                                    <th className="text-left">Jogo</th>
                                    <th>Mercados</th>
                                    <th>Status</th>
                                </tr>
                                </thead>
                                <tbody>
                                {eventos.map((evento) => {
                                    const desativado = eventosDesativados.includes(evento.external_id);

                                    return (
                                        <tr key={evento.external_id}>
                                            <td className="align-middle">
                                                {dayjs(evento.start_at).format('DD/MM/YYYY')} <br/>
                                                {dayjs(evento.start_at).format('HH:mm')}
                                            </td>
                                            <td className="align-middle">
                                                {evento.sport?.name} <br/>
                                                {evento.league?.name}
                                            </td>
                                            <td className="text-left align-middle">
                                                {evento.team_home?.name} x {evento.team_out?.name}
                                            </td>
                                            <td className="align-middle">
                                                <button
                                                    className="btn btn-info"
                                                    onClick={() => abrirMercados(evento)}
                                                >
                                                    Ver mercados
                                                </button>
                                            </td>
                                            <td className="align-middle">
                                                <button
                                                    className={`btn ${desativado ? 'btn-danger' : 'btn-success'}`}
                                                    disabled={!podeEditar || carregaPut}
                                                    onClick={() => alternarEvento(evento, desativado)}
                                                >
                                                    {desativado ? 'Bloqueado' : 'Ativo'}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>

                            {eventosMeta && (
                                <Pagination
                                    currentPage={paginaEventos}
                                    totalCount={eventosMeta.total}
                                    pageSize={eventosMeta.per_page}
                                    onPageChange={(pagina) => requestEventos(pagina)}
                                />
                            )}
                        </div>
                    )}

                    {modalMercados()}
                </>
            )}
        </>
    );
}
